import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type MarketingOptInMethod = 'MANUAL' | 'BULK_IMPORT' | 'CSV' | 'FORM' | 'EVENT_BOOKING';
export type MarketingSuppressionReason = 'UNSUBSCRIBE' | 'BOUNCE' | 'MANUAL' | 'COMPLAINT';

export interface MarketingContact {
  id: string;
  contact_ref_id: string | null;
  contact_type: 'candidate' | 'contact' | 'external';
  email: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  opted_in_at: string;
  opt_in_method: MarketingOptInMethod;
  unsubscribed_at: string | null;
  unsubscribe_reason: string | null;
}

export interface MarketingSuppression {
  id: string;
  email: string;
  reason: MarketingSuppressionReason;
  source: string | null;
  suppressed_at: string;
}

export function useMarketingContacts(opts: { includeUnsubscribed?: boolean } = {}) {
  return useQuery<MarketingContact[]>({
    queryKey: ['marketing-contacts', opts.includeUnsubscribed ?? false],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: MarketingContact[] }>('/marketing-contacts', {
        params: opts.includeUnsubscribed ? { include_unsubscribed: 'true' } : undefined,
      });
      return data.data;
    },
  });
}

export function useMarketingContactCount() {
  return useQuery<{ active: number }>({
    queryKey: ['marketing-contacts', 'count'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { active: number } }>('/marketing-contacts/count');
      return data.data;
    },
  });
}

/** This billing month's count against what the organisation has paid for (JT1-1865). */
export interface MarketingContactUsage {
  used: number;
  /** null when JobTetris sets no limit. 0 means no marketing-contact pack. */
  limit: number | null;
  period_start: string;
  period_end: string | null;
}

export function useMarketingContactUsage() {
  return useQuery<MarketingContactUsage>({
    queryKey: ['marketing-contacts', 'usage'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: MarketingContactUsage }>('/marketing-contacts/usage');
      return data.data;
    },
  });
}

export interface AddMarketingContactPayload {
  /** Optional — omit for freeform newsletter subscribers not linked to JT. */
  contact_ref_id?: string | null;
  /** Optional too; defaults to 'external' when no contact_ref_id given. */
  contact_type?: 'candidate' | 'contact' | 'external' | null;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  opt_in_method?: MarketingOptInMethod;
}

export function useAddMarketingContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddMarketingContactPayload) => {
      const { data } = await apiClient.post<{ data: MarketingContact }>('/marketing-contacts', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing-contacts'] }),
  });
}

export interface BulkImportResult {
  added: number;
  skipped: Array<{ email: string; reason: string }>;
  /** Zero-based index of the row that would have gone past the limit. */
  aborted_at_row?: number;
  limit_message?: string;
}

export function useBulkImportMarketingContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: AddMarketingContactPayload[]) => {
      const { data } = await apiClient.post<{ data: BulkImportResult }>(
        '/marketing-contacts/import',
        { rows }
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing-contacts'] }),
  });
}

export function useRemoveMarketingContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/marketing-contacts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing-contacts'] }),
  });
}

export function useMarketingSuppressions() {
  return useQuery<MarketingSuppression[]>({
    queryKey: ['marketing-suppressions'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: MarketingSuppression[] }>('/marketing-contacts/suppressions');
      return data.data;
    },
  });
}

export function useUnsuppressMarketing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      await apiClient.delete(`/marketing-contacts/suppressions/${encodeURIComponent(email)}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing-suppressions'] });
      qc.invalidateQueries({ queryKey: ['marketing-contacts'] });
    },
  });
}
